import { sanitizeCSVValue } from '@root/helpers/csv/sanitize-csv-value';

/**
 * CSV writer for the review export: findings and the review state layered on
 * top of them (resolution, AI verdict, grade, comments), one row per finding
 * plus one per run-level feedback entry, one per run step (tokens, seconds)
 * and one per audited check verdict — distinguished by `row_type` rather
 * than by a second table, so the file stays a single rectangle Excel can
 * filter.
 *
 * The rows come from the review export endpoint
 * (`GET /eval_configs?export=review`), whose row type (in
 * `domain/types/response/evals/EvalResponses.ts`) must stay in
 * step with `CSV_COLUMNS` below.
 */
export const CSV_COLUMNS = [
  'row_type',
  'finding_id',
  'superseded_by',
  'severity',
  'title',
  'description',
  'check_id',
  'check_text',
  'block_name',
  'status',
  'resolution_source',
  'resolution_note',
  'ai_resolution_verdict',
  'ai_resolution_rationale',
  'grade',
  'grade_note',
  'comments',
  'created_at',
  'run_id',
  'run_created_at',
  'contract_id',
  'contract',
  'contract_ref',
  'eval_config_name',
  'eval_config_version',
  // Appended (not interleaved) so spreadsheets built on the original layout
  // keep their column positions. Broker-feedback triage: who resolved what,
  // when, and when the AI re-check ran. Offline config comparison: the lens
  // or check that produced the finding, the SME grader, run status and
  // identity, plus STEP / CHECK_RESULT rows for cost and audit verdicts.
  'step_key',
  'check_verdict',
  'resolved_by',
  'resolved_by_email',
  'resolved_at',
  'ai_resolution_at',
  'graded_by',
  'graded_by_email',
  'graded_at',
  'step_label',
  'step_status',
  'input_tokens',
  'output_tokens',
  'duration_seconds',
  'run_status',
  'is_latest_run',
  'eval_config_id',
  'model_id',
  'workflow_type',
] as const;

export type CsvColumn = (typeof CSV_COLUMNS)[number];
export type CsvRow = Partial<Record<CsvColumn, string | number | boolean>>;

/**
 * Quote every field unconditionally. It costs a few bytes and removes the
 * whole class of "this one had a comma in it" bugs; embedded quotes are
 * doubled and newlines survive inside the quotes, per RFC 4180.
 */
export const escapeCsvField = (value: unknown): string => {
  const sanitized = sanitizeCSVValue(value ?? '');
  return `"${String(sanitized ?? '').replace(/"/g, '""')}"`;
};

/** CRLF line endings: what Excel expects from a .csv on every platform. */
export const toCsv = (rows: CsvRow[], columns: readonly string[] = CSV_COLUMNS): string =>
  [columns, ...rows.map((row) => columns.map((column) => (row as Record<string, unknown>)[column]))]
    .map((cells) => cells.map(escapeCsvField).join(','))
    .join('\r\n');

/** `pbqa-review-<config name or scope>-<date>.csv` */
export const buildCsvFilename = (scope: string, date: string): string => {
  const slug = scope.replace(/[^a-zA-Z0-9-_]+/g, '-').replace(/^-+|-+$/g, '') || 'export';
  return `pbqa-review-${slug}-${date}.csv`;
};

/** Hands the file to the browser via a throwaway object URL. */
export const downloadCsv = (filename: string, csv: string): void => {
  // The BOM is what makes Excel read the file as UTF-8 rather than mangling
  // any non-ASCII wording lifted out of a contract.
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};
